import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Header from './Header';
import { useNavigate } from 'react-router-dom';
import { Search, X, RefreshCw, ArrowLeft, Trash2, ListOrdered } from 'lucide-react';
import { BACKEND_URL } from './backendConfig';
import { fmtDateTimeSecVN } from './timeZone';
import { useTranslation } from 'react-i18next';
import ModuleHelp from './ModuleHelp';

/* THE GUIDE (Max 01/11: every module has one). It walks the Log view; onBeforeTour puts the screen back on it. */
const TOUR = [
  { selector: '.al-head-actions [data-al-view]', title: 'tourActivity.viewsT', body: 'tourActivity.viewsB' },
  { selector: '.al-filters', title: 'tourActivity.filtersT', body: 'tourActivity.filtersB' },
  { selector: '.al-table thead', title: 'tourActivity.tableT', body: 'tourActivity.tableB' },
  { selector: '.al-table .al-by', title: 'tourActivity.byT', body: 'tourActivity.byB' },
];

// The audit log's module key → its label's key in xActivity.mod; a module not listed prints its own key.
const MODULE_LABEL = {
    project_tracker: 'projectTracker',
    sample_tracker: 'sampleTracker',
    ticket_station: 'ticketStation',
    nfc_portal: 'nfcPortal',
    portal_admin: 'portalAdmin',
    sp_hub: 'spHub',
    cash_report: 'cashReport',
    sop_weekly: 'sopWeekly',
};
const moduleLabel = (t, m) => (MODULE_LABEL[m] ? t(`xActivity.mod.${MODULE_LABEL[m]}`) : m);
const prettyAction = (a) => String(a || '').toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const fmtTs = (ts) => fmtDateTimeSecVN(ts);

const PAGE = 100;
/* a date moved earlier (Jay 30/10) — amber, and red when it is more than 7 days back from the day it was entered */
const ROW_TONE = { DATE_MOVED_EARLIER: 'al-row-amber', BACKDATE_FLAGGED: 'al-row-red' };

/* WHAT WAS DELETED (Jay 30/10: "keep it with a 'Deleted' mark instead of removing it") — every sample round, SKU or
   project deleted on the trackers, kept as it was with who and when; the data opens below the row. */
function DeletedList({ t }) {
    const [rows, setRows] = useState(null);
    const [q, setQ] = useState('');
    const [open, setOpen] = useState(null);
    const [error, setError] = useState('');
    useEffect(() => {
        const h = setTimeout(() => {
            axios.get(`${BACKEND_URL}/api/portal/deleted-records`, { params: q.trim() ? { q: q.trim() } : {} })
                .then((r) => setRows(r.data.rows || [])).catch((e) => setError(e.response?.data?.error || t('xActivity.loadFailed')));
        }, 300);
        return () => clearTimeout(h);
    }, [q, t]);
    return (
        <>
            <div className="al-filters">
                <div className="al-search"><Search size={16} /><input placeholder={t('xActivity.delSearch')} value={q} onChange={(e) => setQ(e.target.value)} /></div>
                <span className="al-count">{rows ? t('xActivity.delCount', { n: rows.length }) : ''}</span>
            </div>
            <p className="al-del-hint">{t('xActivity.delHint')}</p>
            {error && <div className="al-alert error">{error}</div>}
            <div className="al-table-scroll">
                <table className="al-table">
                    <thead><tr><th>{t('xActivity.col.time')}</th><th>{t('xActivity.col.area')}</th><th>{t('xActivity.delKind')}</th><th>{t('xActivity.col.entity')}</th><th>{t('xActivity.col.by')}</th><th /></tr></thead>
                    <tbody>
                        {(rows || []).map((r) => (
                            <React.Fragment key={r.deleted_pk}>
                                <tr className="al-row-deleted">
                                    <td className="al-nowrap al-muted">{fmtTs(r.deleted_at)}</td>
                                    <td><span className="al-chip">{moduleLabel(t, r.module)}</span></td>
                                    <td><span className="al-del-tag">{t('xActivity.deletedTag')}</span> {t(`xActivity.kind_${r.kind}`, r.kind)}</td>
                                    <td className="al-mono">{r.title || r.entity_key}</td>
                                    <td>{r.deleted_by || '—'}</td>
                                    <td><button className="al-btn ghost small" onClick={() => setOpen(open === r.deleted_pk ? null : r.deleted_pk)} data-al-delopen={r.entity_key}>
                                        {open === r.deleted_pk ? t('xActivity.delHide') : t('xActivity.delShow')}</button></td>
                                </tr>
                                {/* what it held, as the screen would say it (Max 30/10: no raw JSON for users) */}
                                {open === r.deleted_pk && (
                                    <tr><td colSpan={6} className="al-del-cell">
                                        <div className="al-del-box" data-al-delbox={r.entity_key}>
                                            <dl className="al-del-fields">
                                                {(r.fields || []).map((f) => <div key={f.label}><dt>{f.label}</dt><dd>{f.value}</dd></div>)}
                                            </dl>
                                            {(r.sections || []).map((sec) => (
                                                <div key={sec.title} className="al-del-sec">
                                                    <h4>{sec.title} <span>({sec.rows.length})</span></h4>
                                                    <table className="al-del-sub"><thead><tr>{sec.columns.map((c) => <th key={c}>{c}</th>)}</tr></thead>
                                                        <tbody>{sec.rows.map((row, i) => <tr key={i}>{row.map((v, k) => <td key={k}>{v || '—'}</td>)}</tr>)}</tbody></table>
                                                </div>
                                            ))}
                                            {!(r.fields || []).length && <span className="al-muted">—</span>}
                                        </div>
                                    </td></tr>
                                )}
                            </React.Fragment>
                        ))}
                        {rows && !rows.length && <tr><td colSpan={6} className="al-empty">{t('xActivity.delNone')}</td></tr>}
                        {!rows && !error && <tr><td colSpan={6} className="al-empty">{t('xActivity.loading')}</td></tr>}
                    </tbody>
                </table>
            </div>
        </>
    );
}

function ActivityLog() {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [modules, setModules] = useState([]);
    const [actions, setActions] = useState([]);
    const [moduleF, setModuleF] = useState('');
    const [actionF, setActionF] = useState('');
    const [search, setSearch] = useState('');
    const [dq, setDq] = useState('');            // debounced search
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [view, setView] = useState('log');      // log | deleted
    const navigate = useNavigate();

    // debounce the search box
    useEffect(() => { const t = setTimeout(() => setDq(search.trim()), 400); return () => clearTimeout(t); }, [search]);

    const fetchLog = useCallback(async (offset, replace) => {
        setLoading(true); setError('');
        try {
            const params = { limit: PAGE, offset };
            if (moduleF) params.module = moduleF;
            if (actionF) params.action = actionF;
            if (dq) params.q = dq;
            const res = await axios.get(`${BACKEND_URL}/api/portal/audit-log`, { params });
            setTotal(res.data.total || 0);
            setModules(res.data.modules || []);
            setActions(res.data.actions || []);
            setRows((prev) => (replace ? res.data.rows : [...prev, ...(res.data.rows || [])]));
        } catch (err) {
            setError(err.response?.data?.error || t('xActivity.loadFailed'));
        } finally { setLoading(false); }
    // `t` is left out on purpose: a language switch must not reload the log.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [moduleF, actionF, dq]);

    useEffect(() => { fetchLog(0, true); }, [fetchLog]);

    const hasFilter = moduleF || actionF || search;
    const clearFilters = () => { setModuleF(''); setActionF(''); setSearch(''); };

    const moduleOptions = useMemo(() => modules, [modules]);

    return (
        <div className="al-page">
            <Header />
            <div className="al-shell">
                <div className="al-card">
                    <div className="al-head">
                        <div>
                            <h1>{t('xActivity.title')}</h1>
                            <p>{t('xActivity.sub')}</p>
                        </div>
                        <div className="al-head-actions">
                            <button className="al-btn ghost" onClick={() => navigate('/')}><ArrowLeft size={16} /> {t('xActivity.back')}</button>
                            <button className={`al-btn ghost${view === 'log' ? ' on' : ''}`} onClick={() => setView('log')} data-al-view="log"><ListOrdered size={15} /> {t('xActivity.viewLog')}</button>
                            <button className={`al-btn ghost${view === 'deleted' ? ' on' : ''}`} onClick={() => setView('deleted')} data-al-view="deleted"><Trash2 size={15} /> {t('xActivity.viewDeleted')}</button>
                            <button className="al-btn ghost" onClick={() => fetchLog(0, true)}><RefreshCw size={15} /> {t('xActivity.reload')}</button>
                            <ModuleHelp steps={TOUR} tourKey="activityLog" ready={!loading && rows.length > 0} onBeforeTour={() => setView('log')} />
                        </div>
                    </div>

                    {view === 'deleted' ? <DeletedList t={t} /> : <>
                    <div className="al-filters">
                        <div className="al-search">
                            <Search size={16} />
                            <input placeholder={t('xActivity.searchPh')} value={search} onChange={(e) => setSearch(e.target.value)} />
                        </div>
                        <select value={moduleF} onChange={(e) => setModuleF(e.target.value)}>
                            <option value="">{t('xActivity.allAreas')}</option>
                            {moduleOptions.map((m) => <option key={m} value={m}>{moduleLabel(t, m)}</option>)}
                        </select>
                        <select value={actionF} onChange={(e) => setActionF(e.target.value)}>
                            <option value="">{t('xActivity.allActions')}</option>
                            {actions.map((a) => <option key={a} value={a}>{prettyAction(a)}</option>)}
                        </select>
                        {hasFilter && <button className="al-clear" onClick={clearFilters}><X size={14} /> {t('xActivity.clear')}</button>}
                        <span className="al-count">{t('xActivity.count', { shown: rows.length, total })}</span>
                    </div>

                    {error && <div className="al-alert error">{error}</div>}

                    <div className="al-table-scroll">
                        <table className="al-table">
                            <thead>
                                <tr>
                                    <th>{t('xActivity.col.time')}</th><th>{t('xActivity.col.area')}</th><th>{t('xActivity.col.action')}</th><th>{t('xActivity.col.by')}</th><th>{t('xActivity.col.entity')}</th><th>{t('xActivity.col.field')}</th><th>{t('xActivity.col.oldValue')}</th><th>{t('xActivity.col.newValue')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => {
                                    const hasDiff = r.old_value || r.new_value;
                                    return (
                                        <tr key={r.audit_id} className={ROW_TONE[r.action] || ''}>
                                            <td className="al-nowrap al-muted">{fmtTs(r.ts)}</td>
                                            <td><span className="al-chip">{moduleLabel(t, r.module)}</span></td>
                                            <td><span className="al-action">{prettyAction(r.action)}</span></td>
                                            {/* who AND through what (Max 30/10: "ai thực hiện thay đổi thông qua đâu … để có bằng chứng"): the
                                                door, the chat request for a Catchy change, the address and browser. Rows before 30/10 have no door. */}
                                            <td className="al-by">
                                                <div>{r.actor || '—'}</div>
                                                {r.via ? (
                                                    <div className="al-via">
                                                        <span className={`al-door d-${r.via === 'Catchy' ? 'catchy' : r.via === 'System' ? 'system' : 'screen'}`}>
                                                            {r.via === 'Catchy' ? t('xActivity.viaCatchy') : r.via === 'System' ? t('xActivity.viaSystem') : t('xActivity.viaScreen')}
                                                        </span>
                                                        {r.via_said && <q className="al-said" title={r.via_said}>{r.via_said}</q>}
                                                        {(r.client_ip || r.client_ua) && <span className="al-muted al-client">{[r.client_ip, r.client_ua].filter(Boolean).join(' · ')}</span>}
                                                    </div>
                                                ) : <div className="al-via al-muted">{t('xActivity.viaNone')}</div>}
                                            </td>
                                            <td className="al-mono">{r.entity_id || '—'}</td>
                                            {/* An edit writes one row per changed field, so `field` names it. Create /
                                                delete / email events have no field and fall back to the detail text. */}
                                            <td className="al-field">{r.field || <span className="al-muted">—</span>}</td>
                                            {hasDiff ? (
                                                <>
                                                    <td className="al-old">{r.old_value ? <pre>{r.old_value}</pre> : <span className="al-muted">—</span>}</td>
                                                    <td className="al-new">{r.new_value ? <pre>{r.new_value}</pre> : <span className="al-muted">—</span>}
                                                        {ROW_TONE[r.action] && r.detail && <div className="al-backdate">{r.detail}</div>}</td>
                                                </>
                                            ) : (
                                                <td className="al-detail" colSpan={2}>{r.detail || '—'}</td>
                                            )}
                                        </tr>
                                    );
                                })}
                                {!rows.length && !loading && <tr><td colSpan={8} className="al-empty">{t('xActivity.empty')}</td></tr>}
                                {loading && !rows.length && <tr><td colSpan={8} className="al-empty">{t('xActivity.loading')}</td></tr>}
                            </tbody>
                        </table>
                    </div>

                    {rows.length < total && (
                        <div className="al-more">
                            <button className="al-btn ghost" disabled={loading} onClick={() => fetchLog(rows.length, false)}>{loading ? t('xActivity.loading') : t('xActivity.loadMore', { n: total - rows.length })}</button>
                        </div>
                    )}
                    </>}
                </div>
            </div>

            <style jsx="true">{`
                .al-page { background: #F4F6F9; min-height: 100vh; }
                .al-shell { margin-top: 64px; padding: 24px; transition: margin-left 180ms ease, width 180ms ease, max-width 180ms ease; }
                @media (min-width: 900px) {
                    body.portal-shell.portal-sidebar-open .al-shell { margin-left: 270px; width: calc(100% - 270px); max-width: calc(100% - 270px); }
                }
                .al-card { background: #fff; border: 1px solid #D8E0EC; border-radius: 14px; max-width: 1200px; margin: 0 auto; padding: 24px 28px; box-shadow: 0 1px 4px rgba(31,56,100,.06); }
                .al-head { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 12px; margin-bottom: 18px; }
                .al-head h1 { font-size: 22px; font-weight: 900; color: #1F3864; margin: 0; }
                .al-head p { margin: 4px 0 0; font-size: 13px; color: #6B7280; }
                .al-head-actions { display: flex; gap: 10px; }
                .al-btn { display: inline-flex; align-items: center; gap: 7px; border-radius: 8px; padding: 8px 14px; font-size: 13px; font-weight: 700; cursor: pointer; border: 1px solid #D8E0EC; background: #fff; color: #1F3864; }
                .al-btn:hover { background: #F1F5F9; }
                .al-btn:disabled { opacity: .6; cursor: not-allowed; }

                .al-filters { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; margin-bottom: 16px; }
                .al-search { display: flex; align-items: center; gap: 8px; border: 1px solid #D8E0EC; border-radius: 9px; padding: 8px 12px; flex: 1 1 280px; min-width: 220px; color: #6B7280; }
                .al-search input { border: none; outline: none; font-size: 14px; width: 100%; background: transparent; color: #111827; }
                .al-filters select { border: 1px solid #D8E0EC; border-radius: 9px; padding: 9px 12px; font-size: 14px; background: #fff; color: #1F3864; font-weight: 600; cursor: pointer; }
                .al-clear { display: inline-flex; align-items: center; gap: 6px; border: 1px solid #F5C2C2; background: #FDECEC; color: #B42318; border-radius: 9px; padding: 8px 12px; font-size: 13px; font-weight: 700; cursor: pointer; }
                .al-count { font-size: 13px; color: #6B7280; font-weight: 600; margin-left: auto; }

                .al-alert.error { background: #FDECEC; border: 1px solid #F5C2C2; color: #B42318; border-radius: 8px; padding: 10px 14px; font-size: 13px; font-weight: 600; margin-bottom: 14px; }

                .al-table-scroll { overflow-x: auto; }
                .al-table { width: 100%; border-collapse: separate; border-spacing: 0; }
                .al-table th, .al-table td { padding: 10px 12px; text-align: left; border-bottom: 1px solid #EEF2F7; font-size: 13.5px; vertical-align: top; }
                .al-table th { background: #F1F5F9; color: #334155; font-weight: 800; font-size: 12.5px; white-space: nowrap; }
                .al-table tbody tr:hover td { background: #FAFBFE; }
                .al-nowrap { white-space: nowrap; }
                .al-muted { color: #6B7280; }
                .al-by { min-width: 170px; }
                .al-via { display: flex; flex-direction: column; gap: 2px; margin-top: 3px; font-size: 11.5px; }
                .al-door { align-self: flex-start; font-size: 11px; font-weight: 700; padding: 1px 7px; border-radius: 9px; }
                .al-door.d-screen { background: #e0ecff; color: #1d4ed8; }
                .al-door.d-catchy { background: #ccfbf1; color: #0f766e; }
                .al-door.d-system { background: #f1f5f9; color: #475569; }
                .al-said { font-style: italic; color: #0f766e; max-width: 260px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: block; }
                .al-client { font-size: 11px; }
                .al-mono { font-family: ui-monospace, monospace; color: #475569; }
                .al-detail { color: #475569; max-width: 420px; word-break: break-word; }
                .al-old pre, .al-new pre { margin: 0; font-family: inherit; white-space: pre-wrap; word-break: break-word; font-size: 12.5px; padding: 4px 8px; border-radius: 6px; }
                .al-old pre { background: #FDECEC; color: #9B2C2C; }
                .al-new pre { background: #E4F6EC; color: #15803D; }
                .al-old, .al-new { max-width: 280px; }
                /* Field names the column that changed — kept narrow and unwrapped so a reader can
                   scan down the list and see which fields were touched. */
                .al-field { white-space: nowrap; font-weight: 600; color: #334155; }
                .al-chip { font-size: 11.5px; font-weight: 700; background: #EAF0F8; color: #1F3864; padding: 2px 9px; border-radius: 20px; white-space: nowrap; }
                .al-action { font-weight: 700; color: #1F3864; }
                .al-row-amber td { background: #FFFBEB; }
                .al-row-red td { background: #FEF2F2; }
                .al-row-red .al-action { color: #B91C1C; }
                .al-backdate { margin-top: 4px; font-size: 12px; color: #92400E; }
                .al-row-red .al-backdate { color: #B91C1C; font-weight: 600; }
                .al-btn.on { background: #1F3864; color: #fff; border-color: #1F3864; }
                .al-btn.on svg, .al-btn.on svg * { color: #fff; }   /* the global colour rule reaches the paths (svg-icon-currentcolor) */
                .al-btn.small { padding: 4px 10px; font-size: 12.5px; }
                .al-del-tag { display: inline-block; padding: 1px 7px; border-radius: 4px; background: #FEE2E2; color: #B91C1C; font-weight: 700; font-size: 11.5px; margin-right: 4px; }
                .al-del-cell { background: #FBFCFE; }
                .al-del-box { border: 1px solid #E2E8F0; border-left: 4px solid #F87171; border-radius: 8px; background: #fff; padding: 14px 16px; }
                .al-del-fields { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 10px 20px; margin: 0; }
                .al-del-fields dt { font-size: 11.5px; font-weight: 700; color: #64748B; text-transform: uppercase; letter-spacing: .03em; }
                .al-del-fields dd { margin: 2px 0 0; font-size: 13.5px; color: #0F172A; overflow-wrap: anywhere; }
                .al-del-sec { margin-top: 14px; }
                .al-del-sec h4 { margin: 0 0 6px; font-size: 13px; color: #1F3864; }
                .al-del-sec h4 span { color: #94A3B8; font-weight: 600; }
                .al-del-sub { border-collapse: collapse; font-size: 12.5px; min-width: 50%; }
                .al-del-sub th { text-align: left; background: #F1F5F9; color: #334155; padding: 6px 10px; font-weight: 700; }
                .al-del-sub td { padding: 6px 10px; border-bottom: 1px solid #EEF2F7; }
                .al-del-hint { margin: 0 0 10px; color: #64748B; font-size: 13px; }
                .al-empty { text-align: center; color: #9CA3AF; padding: 28px; }
                .al-more { text-align: center; margin-top: 16px; }
            `}</style>
        </div>
    );
}

export default ActivityLog;
